/**
 * `approvalChains.*` — configure the approval engine (ADR-0031).
 *
 * Two reusable concepts: **chains** (named flows) and **bindings** (conditional
 * attachment of a chain to a module). All procedures are gated by
 * `permissionGate("approvals.manage")` in the router. `listActions` returns the
 * gateable-action catalog (each module's field catalog) that drives the builder;
 * `previewChain` resolves a chain against a scenario for the live preview.
 */
import { z } from "zod";
import { uuid } from "../_common.js";

// ── shared chain / binding shapes (mirror the persisted JSON in @flint/db) ──
export const approverTargetType = z.enum([
  "owner",
  "manager",
  "dept_head_each",
  "dept_head",
  "role",
  "person",
]);

export const approverTarget = z.object({
  type: approverTargetType,
  /** dept name / role key / user id, per target type. */
  value: z.string().max(120).optional(),
});

export const stageRule = z.union([
  z.literal("any"),
  z.literal("all"),
  z.object({ quorum: z.number().int().positive() }),
]);

export const chainStage = z
  .object({
    rule: stageRule,
    approvers: z.array(approverTarget).min(1).max(20),
    notifyApprovers: z.boolean().default(true),
  })
  .superRefine((stage, ctx) => {
    // A quorum can't exceed the number of approver targets in its step — the
    // wire-level first line of the same guard the domain enforces
    // (`validateChainStructure`). Deeper reference checks stay in the domain.
    if (typeof stage.rule === "object" && stage.rule.quorum > stage.approvers.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Quorum can't exceed the number of approvers in the step.",
        path: ["rule", "quorum"],
      });
    }
  });

export const chainStages = z.array(chainStage).max(10);

export const bindingCondition = z.object({
  op: z.enum(["any", "above", "atleast", "below"]),
  /** Threshold for the module's primary numeric (paise / whole % / count). */
  val: z.number().nonnegative().optional(),
  extra: z
    .array(
      z.object({
        key: z.string().min(1).max(40),
        op: z.string().min(1).max(16),
        value: z.union([z.string().max(120), z.number()]),
      }),
    )
    .max(8)
    .optional(),
});

// ── action catalog (read) ────────────────────────────────────────────
const approvalFieldDef = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(["money", "percent", "number", "enum", "enum_multi", "entity"]),
  options: z.array(z.string()).optional(),
});

export const listActionsOutput = z.object({
  actions: z.array(
    z.object({
      actionType: z.string(),
      label: z.string(),
      description: z.string(),
      primary: z
        .object({ key: z.string(), label: z.string(), type: z.enum(["money", "percent", "number"]) })
        .nullable(),
      fields: z.array(approvalFieldDef),
    }),
  ),
});

// ── chains ────────────────────────────────────────────────────────────
const chainRow = z.object({
  id: uuid,
  key: z.string().nullable(),
  name: z.string(),
  stages: chainStages,
  isSystem: z.boolean(),
  /** Number of (action) modules this chain is currently attached to. */
  usedByCount: z.number().int().nonnegative(),
});

export const listChainsOutput = z.object({ chains: z.array(chainRow) });

export const upsertChainInput = z.object({
  id: uuid.optional(),
  name: z.string().trim().min(2).max(120),
  stages: chainStages,
});
export const upsertChainOutput = z.object({ id: uuid });

export const archiveChainInput = z.object({ id: uuid });
export const archiveChainOutput = z.object({ ok: z.boolean() });

// ── bindings ──────────────────────────────────────────────────────────
const bindingRow = z.object({
  id: uuid,
  actionType: z.string(),
  priority: z.number().int(),
  condition: bindingCondition,
  chainId: uuid,
  enabled: z.boolean(),
});

export const listBindingsOutput = z.object({ bindings: z.array(bindingRow) });

export const upsertBindingInput = z.object({
  id: uuid.optional(),
  actionType: z.string().min(1).max(40),
  priority: z.number().int().min(0).max(1000),
  condition: bindingCondition,
  chainId: uuid,
  enabled: z.boolean().default(true),
});
export const upsertBindingOutput = z.object({ id: uuid });

export const removeBindingInput = z.object({ id: uuid });
export const removeBindingOutput = z.object({ ok: z.boolean() });

// ── live preview ──────────────────────────────────────────────────────
export const previewChainInput = z.object({
  /** Preview an existing chain by id, or inline stages (unsaved editor state). */
  chainId: uuid.optional(),
  stages: chainStages.optional(),
  actionType: z.string().min(1).max(40),
  scenario: z.object({
    primary: z.number().nullable(),
    departments: z.array(z.string()).default([]),
    fields: z.record(z.string(), z.union([z.string(), z.number(), z.array(z.string())])).default({}),
  }),
});

export const previewChainOutput = z.object({
  stages: z.array(
    z.object({
      order: z.number().int(),
      rule: stageRule,
      approvers: z.array(z.object({ userId: uuid, name: z.string(), resolvedFrom: z.string() })),
    }),
  ),
});

// ── derived TS types ──────────────────────────────────────────────────
export type ApproverTarget = z.infer<typeof approverTarget>;
export type ChainStageWire = z.infer<typeof chainStage>;
export type BindingConditionWire = z.infer<typeof bindingCondition>;
export type UpsertChainInput = z.infer<typeof upsertChainInput>;
export type UpsertBindingInput = z.infer<typeof upsertBindingInput>;
export type PreviewChainInput = z.infer<typeof previewChainInput>;
export type ListActionsOutput = z.infer<typeof listActionsOutput>;
export type ListChainsOutput = z.infer<typeof listChainsOutput>;
export type ListBindingsOutput = z.infer<typeof listBindingsOutput>;
export type PreviewChainOutput = z.infer<typeof previewChainOutput>;
